<template>
    <FormSection title="Payer Information" type="his-section" :full-height="false" :application-id="application">
        <template #content>
            <div class="row p-2 pb-0">
                <div class="col-12 offset-md-1 col-md-5">
                    <div class="form-group col-12"></div>
                    <label for="referral-race" class="mb-2 pb-1">
                        <span class="badge me-1">A1400</span> Payment Source (Check all that apply)
                        <span class="text-danger">*</span>
                        <a
                            tabindex="0"
                            href="javascript:void(0);"
                            role="button"
                            v-custom-popover="{ content: sanitizeMarkup(payerSourceTooltip) }"
                        >
                            <i class="fas fa-info-circle text-primary font-size-xs"></i>
                        </a>
                    </label>
                    <div class="form-check px-0" v-for="[key, value] of paymentSources">
                        <div class="px-2 w-100">
                            <div class="form-check custom-checkbox form-check-inline">
                                <input
                                    type="checkbox"
                                    class="form-check-input au-target"
                                    :id="`source${value.name}`"
                                    :value="value.value"
                                    :checked="patientDetails.paymentSource?.includes(value.value)"
                                    :disabled="
                                        (patientDetails.paymentSource?.length > 0 &&
                                            !isNoPaymentSourceChecked &&
                                            value.name === 'No payer source') ||
                                        (isNoPaymentSourceChecked && value.name !== 'No payer source')
                                    "
                                    @change="addPaymentSource(value.value)"
                                    @blur="v$.patientDetails.paymentSource.$touch()"
                                />
                                <label class="form-check-label" :for="`source${value.name}`">
                                    <span class="me-1">{{ value.name }}</span>
                                </label>
                            </div>
                        </div>
                    </div>
                    <span v-if="v$.patientDetails.paymentSource.$invalid" class="text-danger mt-1"
                        >Payment Source is required.</span
                    >
                </div>
                <div class="col-12 offset-md-1 col-md-5">
                    <div
                        class="form-group col-12"
                        v-if="!patientDetails.medicareNumberNa && !!patientDetails.medicareNumber"
                    >
                        <label id="Referral_Medicare_Number_Label" for="medicare-number">
                            <span class="badge me-1">A0600B</span> Medicare Number
                        </label>
                        <p>{{ patientDetails.medicareNumber }}</p>
                    </div>
                    <div class="form-group col-12">
                        <label for="mbi-number" id="Mbi_Number_Label">
                            <span class="badge me-1">A0600B</span> Medicare Beneficiary Identifier (MBI)
                            <span v-if="!patientDetails.medicareBeneficiaryIdentificationNA" class="text-danger"
                                >*</span
                            >
                            <a
                                tabindex="0"
                                href="javascript:void(0);"
                                role="button"
                                v-custom-popover="{ content: sanitizeMarkup(mbiTooltip) }"
                            >
                                <i class="fas fa-info-circle text-primary font-size-xs"></i>
                            </a>
                        </label>
                        <input
                            class="form-control"
                            maxlength="50"
                            name="mbi-number"
                            type="text"
                            placeholder="Enter Medicare Beneficiary Identifier (MBI)"
                            :value="formatToUppercase(patientDetails.medicareBeneficiaryIdentification)"
                            :disabled="patientDetails.medicareBeneficiaryIdentificationNA"
                            @change="(e) => handleMedicareNumber(e)"
                            @blur="v$.patientDetails.medicareBeneficiaryIdentification.$touch"
                            :class="`${
                                !v$.patientDetails.medicareBeneficiaryIdentification.$error ? '' : 'field-invalid'
                            }`"
                        />
                        <div class="form-group row">
                            <div class="col-12">
                                <div class="form-check custom-checkbox form-check-inline mt-2">
                                    <input
                                        type="checkbox"
                                        id="referral-medicare-beneficiary-identification-na"
                                        class="form-check-input au-target"
                                        v-model="patientDetails.medicareBeneficiaryIdentificationNA"
                                    />
                                    <label
                                        class="form-check-label"
                                        for="referral-medicare-beneficiary-identification-na"
                                    >
                                        <span class="me-1">N/A- Not Applicable</span>
                                    </label>
                                </div>
                            </div>
                        </div>
                        <span v-if="v$.patientDetails.medicareBeneficiaryIdentification.$error" class="field-invalid"
                            >Medicare Beneficiary Identifier is required.</span
                        >
                    </div>
                    <div class="form-group col-12">
                        <i class="fas fa-question-circle"></i>
                        <a
                            href="https://www.cms.gov/Medicare/New-Medicare-Card/Providers/Providers-and-office-managers.html"
                            target="_blank"
                        >
                            Click here to review CMS guidelines on the New Medicare Beneficiary Identifier
                        </a>
                    </div>
                    <div class="form-group col-12">
                        <label for="medicaid-number"> <span class="badge me-1">A0700</span> Medicaid Number </label>
                        <input
                            class="form-control"
                            maxlength="50"
                            placeholder="Enter Medicaid Number"
                            name="medicaid-number"
                            type="text"
                            v-model="patientDetails.medicaidNumber"
                            :disabled="
                                otherMedicaid == otherMedicaidEnum?.MedicaidPending ||
                                otherMedicaid == otherMedicaidEnum?.NotAMedicaid
                            "
                        />
                        <div class="form-group row ms-1">
                            <div class="form-check custom-radio form-check-inline mt-2">
                                <input
                                    type="radio"
                                    id="referral-medicaid-pending"
                                    name="other-medicaid"
                                    class="form-check-input au-target"
                                    :value="otherMedicaidEnum.MedicaidPending"
                                    :checked="otherMedicaid === otherMedicaidEnum.MedicaidPending"
                                    @click="otherMedicaidRadioClick(otherMedicaidEnum.MedicaidPending)"
                                />
                                <label class="form-check-label mb-1" for="referral-medicaid-pending">
                                    <span class="me-1">Medicaid is pending</span>
                                </label>
                            </div>
                        </div>
                        <div class="form-group row ms-1">
                            <div class="form-check custom-radio form-check-inline mt-2">
                                <input
                                    type="radio"
                                    id="referral-no-medicaid"
                                    name="other-medicaid"
                                    class="form-check-input au-target"
                                    :value="otherMedicaidEnum?.NotAMedicaid"
                                    :checked="otherMedicaid === otherMedicaidEnum?.NotAMedicaid"
                                    @click="otherMedicaidRadioClick(otherMedicaidEnum?.NotAMedicaid)"
                                />
                                <label class="form-check-label" for="referral-no-medicaid">
                                    <span class="me-1">Not a Medicaid Recipient</span>
                                </label>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="col-12 col-md-10 offset-md-1 ps-0">
                    <EligibilityCheck
                        :first-name="patientDetails.firstName"
                        :last-name="patientDetails.lastName"
                        :gender="patientDetails.gender"
                        :location-id="patientDetails.locationId"
                        :date-of-birth="patientDetails.dateOfBirth"
                        :medicare-number="patientDetails.medicareNumber"
                        :mbi-number="patientDetails.medicareBeneficiaryIdentification"
                        :medicare-number-na="patientDetails.medicareNumberNA"
                        :mbi-number-na="patientDetails.medicareBeneficiaryIdentificationNA"
                        :application="Number(application)"
                        :account-id="accountId"
                        :missing-url="eligibilityMissingUrl"
                    />
                </div>
                <div class="col-12 col-md-10 offset-md-1 row g-0" v-if="patientId">
                    <PayerTable
                        :add-new-payer="addNewPayer"
                        :getCurrentPatientPayers="getCurrentPatientPayers"
                        :get-patient-payers="getPatientPayers"
                        :update-patient-payer="updatePatientPayer"
                        :delete-patient-payer="deletePatientPayer"
                    />
                </div>
            </div>
        </template>
    </FormSection>
</template>

<script src="./PatientPayerInformation.ts"></script>
